import { ApplicationCategory } from "../model/applications/application-category";
import { IAppSession } from "../model/appSessions/appSession";
import { ApplicationFeatures } from "../model/applications/application-features";
import { IApplication, IApplicationClassification, UPLOADER_APPLICATION_ID } from "../model/applications/applications";
import { AppStatus } from "../model/applications/application-status";
import { ApplicationFamilySlug } from "../model/applications/application-family-slug";

export class ApiApplicationUtilities {
  constructor() { }

  static isWes(appsession: IAppSession): boolean {
    const category = this.getCategory(appsession);
    return this.isWesCategory(category);
  }

  static isWesCategory(category: string): boolean {
    return category === ApplicationCategory.WES;
  }

  static isIca(appsession: IAppSession): boolean {
    const category = this.getCategory(appsession);
    return this.isIcaCategory(category);
  }

  static isIcaWorkflowSession(appsession: IAppSession): boolean {
    return this.getCategory(appsession) === ApplicationCategory.ICA_WORKFLOW_SESSION_TRACKING;
  }

  static isIcaCategory(category: string): boolean {
    return [
      ApplicationCategory.ICA_ANALYSIS_TRACKING,
      ApplicationCategory.ICA_PIPELINE,
      ApplicationCategory.ICA_WORKFLOW_SESSION_TRACKING,
      // The app store contains apps of type RUN_PLANNING_APP, which behave like ICA apps. Any resulting appsessions will be linked
      // to the app with type ICA_WORKFLOW_SESSION_TRACKING
      ApplicationCategory.RUN_PLANNING_APP
    ].includes(category);
  }

  static isWesOrIcaCategory(category: string): boolean {
    return this.isWesCategory(category) || this.isIcaCategory(category);
  }

  static isSessionTrackingCategory(category: string): boolean {
    return category == ApplicationCategory.ICA_ANALYSIS_TRACKING ||
        category == ApplicationCategory.ICA_WORKFLOW_SESSION_TRACKING;
  }

  static isAppLaunchable(app: IApplication): boolean {
    if (!app) { return false; }
    const isRestricted = !!app.AppLaunchRestriction && app.AppLaunchRestriction.isRestricted;
    return !isRestricted
      && !this.isSessionTrackingCategory(app.Category)
      && app.PublishStatus !== AppStatus.DISABLED
      && app.PublishStatus !== AppStatus.DELETED
      && app.Id !== UPLOADER_APPLICATION_ID;
  }

  /**
   * ICA workflow session tracking appsessions cannot be requeued through the app launch form
   * (the form is unpublished and causes a 403 - see BASE-89095). Instead they are requeued by
   * re-directing to the run planning UI, using the input run of the appsession (see BASE-89231).
   */
  static isAppRequeueableViaRunPlanning(app: IApplication): boolean {
    if (!app) { return false; }
    return app.Category === ApplicationCategory.ICA_WORKFLOW_SESSION_TRACKING;
  }

  static displayApplicationTypeForAppsOfThisCategory(category: string): boolean {
    const legacyApplicationCategories = [
      ApplicationCategory.NATIVE,
      ApplicationCategory.VISUALIZATION,
      ApplicationCategory.ANALYTIC,
      ApplicationCategory.INSTANCE,
      ApplicationCategory.QUALITY,
      ApplicationCategory.WORKFLOW,
      ApplicationCategory.OTHER
    ];

    return legacyApplicationCategories.includes(category);
  }

  static getCategory(appsession: IAppSession): string {
    return appsession && appsession.Application && appsession.Application.Category;
  }

  /**
   * Whether this appsession is a local analysis (GCU Ola or NextSeq1k Ola) launched from the
   * Instrument Uploader app. Uses AppFamilySlug in addition to Category so the file-upload
   * Imported app (also Category Uploader) is excluded.
   */
  static isLocalAnalysis(appsession: IAppSession): boolean {
    return this.getCategory(appsession) === ApplicationCategory.UPLOADER &&
      !!appsession.Application &&
      appsession.Application.AppFamilySlug === ApplicationFamilySlug.OLA_INSTRUMENT_UPLOADER_APP_FAMILY_SLUG;
  }

  /**
   * Whether HTML reports should render for this appsession.
   * Regular apps (WES/ICA/etc.): enabled purely by the ENABLE_HTML_REPORTS feature flag on the app.
   *
   * Local analyses (Instrument Uploader) are a special case. GCU Ola and NextSeq1k Ola share that
   * app, so the app feature alone cannot distinguish them. For local analyses the caller must also
   * supply OlaHtmlApplicationReports for the input run's instrument platform — that CF is rolled
   * out per instrument so GCU platforms can be enabled without unlocking NextSeq1k Ola.
   * @param appsession
   * @param isOlaHtmlApplicationReportsCodeFeatureEnabled
   */
  static isHtmlReportEnabled(
    appsession: IAppSession,
    isOlaHtmlApplicationReportsCodeFeatureEnabled: boolean = false
  ): boolean {
    const isHtmlFeatureFlagEnabled =
      appsession.Application.Features.indexOf(ApplicationFeatures.ENABLE_HTML_REPORTS) > -1;

    if (this.isLocalAnalysis(appsession)) {
      return isHtmlFeatureFlagEnabled && isOlaHtmlApplicationReportsCodeFeatureEnabled;
    }

    return isHtmlFeatureFlagEnabled;
  }

  static hasComplexComputeCost(appsession: IAppSession): boolean {
    return (appsession.Application.Features.includes(ApplicationFeatures.HAS_COMPLEX_COMPUTE_COST));
  }

  static getClassificationDescription(classificationName: string, appClassifications: IApplicationClassification[]): string {
    if(appClassifications) {
      const foundClassification = appClassifications.find((classification) => classification.Name === classificationName);
      if(foundClassification) {
        return foundClassification.Description;
      }
    }
    return classificationName;
  }

  static getAppStatusVersionLabel(application: IApplication): string {
    switch(application.PublishStatus) {
      case AppStatus.DEVELOPMENT:
      case AppStatus.INITIALIZING:
      case AppStatus.BETA:
      case AppStatus.TESTING:
      case AppStatus.DISABLED:
      case AppStatus.DEPRECATED:
        return`${application.VersionNumber} (${application.PublishStatus})`;
      case AppStatus.DOMAIN_PUBLISHED:
        return`${application.VersionNumber} (Domain published)`;
      // We don't display status of 'Published' because it's default/most common
      case AppStatus.PUBLISHED:
      default:
        return application.VersionNumber;
    }
  }
}
